import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
  effect,
  viewChild,
  ElementRef,
  OnDestroy,
  signal,
  computed,
  untracked,
  inject,
  PLATFORM_ID,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { FlashMapService } from '../../services/flash-map.service';
import {
  FlashMapOptions,
  MindMapData,
  EventType,
  EventData,
  FlashMapNode,
  FlashMapNodeMetricsMap,
  FlashMapAppearanceTheme,
  Direction,
  NoteMeta,
  FlashcardMeta,
  FlashMapPresentationState,
  FlashMapDrawingDocument,
  FlashMapDrawingNodeLayer,
  FlashMapDrawingStroke,
  FlashMapDrawingPoint,
  FlashMapDrawingLineStyle,
} from '../../models/types';
import { FlashMapNodeModel } from '../../models/node.model';
import { FlashcardBadgeRenderer } from '../../renderers/flashcard-badge-renderer';
import { ImageNodeRenderer } from '../../renderers/image-node-renderer';
import { NodeRenderContext, NodeRenderer } from '../../renderers/node-renderer';
import { NodeMetricsRenderer } from '../../renderers/node-metrics-renderer';
import { TextNodeRenderer } from '../../renderers/text-node-renderer';
import { OutlineTreeViewComponent } from '../outline/outline-tree-view.component';
import { OutlineCoordinator } from '../../utils/outline-coordinator';
import {
  fromTabbedOutlineFragment,
  OutlineFragmentNode,
  toTabbedOutlineFragment,
} from '../../utils/outline-adapter';
import { hasMeaningfulNoteText } from '../../utils/note-content';
import { HelpShortcutsComponent } from '../modals/help-shortcuts.component';
import { MarkdownParser } from '../../utils/jsmind.markdown';
import {
  FLASH_MAP_I18N_CONFIG,
  FlashMapLanguage,
  getFlashMapTranslations,
} from '../../i18n/flash-map-i18n';

type FindFilter = 'all' | 'topic' | 'note' | 'outline';
type FindResultType = 'topic' | 'note';
type MapMenuAction =
  | 'back'
  | 'duplicate'
  | 'move'
  | 'move-team-maps'
  | 'publishing-settings'
  | 'versions'
  | 'move-trash'
  | 'share'
  | 'download'
  | 'help';

type FindResult = { id: string; title: string; type: FindResultType; snippet?: string };
type CopiedNodePayload = {
  topic: string;
  data: Record<string, unknown>;
  direction: Direction | string | number;
  expanded: boolean;
  children: CopiedNodePayload[];
};

type PresentationSnapshot = {
  selectedNodeId: string | null;
  zoom: number;
  pan: { x: number; y: number };
};

type PresentationViewport = {
  zoom: number;
  pan: { x: number; y: number };
};

type DrawingTool = 'none' | 'pen' | 'laser' | 'rectangle' | 'ellipse' | 'arrow';
type DrawingNodeLayer = FlashMapDrawingNodeLayer;
type DrawingColorTarget = 'stroke' | 'fill';
type DrawingLineStyle = FlashMapDrawingLineStyle;
type DrawingPoint = FlashMapDrawingPoint;
type LaserDrawingStroke = Omit<FlashMapDrawingStroke, 'tool'> & {
  tool: 'laser';
  isFading?: boolean;
};
type DrawingStroke = FlashMapDrawingStroke | LaserDrawingStroke;
type DrawingInteraction =
  | { kind: 'draw'; strokeId: number }
  | { kind: 'move'; strokeId: number; point: DrawingPoint; initial: DrawingStroke }
  | { kind: 'resize'; strokeId: number; handle: string; initial: DrawingStroke }
  | { kind: 'rotate'; strokeId: number; center: DrawingPoint; initialRotation: number }
  | { kind: 'arrow-end'; strokeId: number; end: 0 | 1 }
  | null;

const EMPTY_PRESENTATION_STATE: FlashMapPresentationState = {
  active: false,
  currentIndex: 0,
  total: 0,
  currentNodeId: null,
};

const PRESENTATION_TARGET_WIDTH_RATIO = 0.85;
const PRESENTATION_TARGET_HEIGHT_RATIO = 0.75;
const PRESENTATION_MAX_ZOOM = 12;
const PRESENTATION_MEASUREMENT_MAX_ATTEMPTS = 30;
const PRESENTATION_TRANSITION_DURATION_MS = 300;
const PRESENTATION_EXIT_ZOOM = 1.4;
const LASER_STROKE_DURATION_MS = 2000;
const DEFAULT_DRAWING_STROKE_WIDTH = 3;
const DRAWING_STROKE_WIDTHS = [2, 3, 5];
const DRAWING_LINE_STYLES: readonly DrawingLineStyle[] = ['solid', 'dashed'];
const DEFAULT_DRAWING_NODE_LAYER: DrawingNodeLayer = 'above-nodes';
const DRAWING_HIT_TARGET_SCREEN_WIDTH = 24;
const DRAWING_ARROW_ENDPOINT_HIT_TARGET_SCREEN_RADIUS = 24;
const DRAWING_COLORS = [
  '#111827',
  '#ef4444',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#3b82f6',
  '#a855f7',
];

@Component({
  selector: 'lib-flash-map',
  templateUrl: './flash-map.component.html',
  styleUrls: ['./flash-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [FlashMapService],
  imports: [CommonModule, OutlineTreeViewComponent, HelpShortcutsComponent],
  host: {
    '(document:keydown)': 'onDocumentKeydown($event)',
    '(document:click)': 'onDocumentClick($event)',
  },
})
export class FlashMapComponent implements OnDestroy {
  private readonly i18nConfig = inject(FLASH_MAP_I18N_CONFIG);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);

  // Input signals
  readonly language = input<FlashMapLanguage | string | null>(null);
  readonly options = input<Partial<FlashMapOptions>>({});
  readonly mindData = input<MindMapData | null>(null);
  readonly nodeMetrics = input<FlashMapNodeMetricsMap>({});
  readonly editable = input<boolean>(false);
  readonly theme = input<string | null>(null);
  readonly appearanceTheme = input<FlashMapAppearanceTheme>('light');

  // Output signals
  readonly onShow = output<EventData>();
  readonly onResize = output<EventData>();
  readonly onEdit = output<EventData>();
  readonly drawingChange = output<FlashMapDrawingDocument>();
  readonly onSelect = output<EventData>();
  readonly flashcardClick = output<{ nodeId: string; count: number }>();
  readonly flashcardPaperClick = output<{ nodeId: string }>();
  readonly flashcardDeckClick = output<{ nodeId: string }>();

  // View child reference
  readonly containerRef = viewChild<ElementRef<HTMLDivElement>>('container');

  // Internal state
  protected readonly initialized = signal(false);
  private readonly outline: OutlineCoordinator;
  protected readonly mode!: OutlineCoordinator['mode'];
  protected readonly outlineText!: OutlineCoordinator['outlineText'];
  protected readonly outlineError!: OutlineCoordinator['outlineError'];
  protected readonly selectedOutlineLine!: OutlineCoordinator['selectedOutlineLine'];
  protected readonly editingOutlineNodeId!: OutlineCoordinator['editingOutlineNodeId'];
  protected readonly outlineEnabled!: OutlineCoordinator['outlineEnabled'];
  readonly outlineTreeNodes!: OutlineCoordinator['outlineTreeNodes'];
  protected readonly helpOpen = signal(false);
  protected readonly noteOpen = signal(false);
  protected readonly noteText = signal('');
  protected readonly noteSubtitle = signal('');
  protected readonly activeNoteNodeId = signal<string | null>(null);
  protected readonly findOpen = signal(false);
  protected readonly findQuery = signal('');
  protected readonly findFilter = signal<FindFilter>('all');
  protected readonly findResults = signal<FindResult[]>([]);
  protected readonly findActiveIndex = signal(0);
  protected readonly translations = computed(() =>
    getFlashMapTranslations(this.language() ?? this.i18nConfig.language),
  );
  protected readonly isDarkAppearanceTheme = computed(() => this.appearanceTheme() === 'dark');
  protected readonly drawingEnabled = computed(() => this.options().drawing?.enabled === true);
  readonly drawingPanelVisible = signal(false);
  protected readonly drawingTool = signal<DrawingTool>('none');
  protected readonly drawingColor = signal('#ef4444');
  protected readonly drawingFillColor = signal<string | null>(null);
  protected readonly drawingColorMenu = signal<DrawingColorTarget | null>(null);
  protected readonly drawingStrokeWidth = signal(DEFAULT_DRAWING_STROKE_WIDTH);
  protected readonly drawingStrokeWidthMenuOpen = signal(false);
  protected readonly drawingLineStyle = signal<DrawingLineStyle>('solid');
  protected readonly drawingLineStyleMenuOpen = signal(false);
  protected readonly drawingStrokes = signal<DrawingStroke[]>([]);
  protected readonly selectedDrawingId = signal<number | null>(null);
  protected readonly selectedDrawing = computed(
    () => this.drawingStrokes().find((stroke) => stroke.id === this.selectedDrawingId()) ?? null,
  );
  protected readonly selectedArrangeableDrawing = computed(() => {
    const selected = this.selectedDrawing();
    return selected && this.isArrangeableDrawing(selected) ? selected : null;
  });
  protected readonly canBringSelectedDrawingForward = computed(() =>
    this.canMoveSelectedArrangeableDrawing('forward'),
  );
  protected readonly canSendSelectedDrawingBackward = computed(() =>
    this.canMoveSelectedArrangeableDrawing('backward'),
  );
  protected readonly drawingViewportTransform = computed(() => {
    const { zoom, pan } = this.flashMapService.viewport();
    return `translate(${pan.x} ${pan.y}) scale(${zoom})`;
  });
  protected readonly drawingLayerBelowTransform = computed(() => this.drawingViewportTransform());
  protected readonly belowNodeVisualStrokes = computed(() =>
    this.drawingStrokes().filter((stroke) => this.shouldRenderDrawingBelowNodes(stroke)),
  );
  protected readonly selectedDrawingArrangePanelStyle = computed(() => {
    const selected = this.selectedArrangeableDrawing();
    if (!selected) {
      return null;
    }
    const bounds = this.drawingRectangle(selected);
    const { zoom, pan } = this.flashMapService.viewport();
    const offsetX = 12;
    const offsetY = 12;
    return {
      left: `${bounds.x * zoom + pan.x + bounds.width * zoom + offsetX}px`,
      top: `${bounds.y * zoom + pan.y - offsetY}px`,
      transform: 'translate(-100%, -100%)',
    };
  });
  protected readonly drawingColors = DRAWING_COLORS;
  protected readonly drawingStrokeWidths = DRAWING_STROKE_WIDTHS;
  protected readonly drawingLineStyles = DRAWING_LINE_STYLES;
  protected readonly hasPersistentDrawings = computed(() =>
    this.drawingStrokes().some((stroke) => stroke.tool !== 'laser'),
  );
  protected readonly rootTitle = signal('');
  readonly presentationState = signal<FlashMapPresentationState>(EMPTY_PRESENTATION_STATE);
  protected readonly presentationSlideLabel = computed(() => {
    const state = this.presentationState();
    return `${this.translations().presentation.slide} ${state.currentIndex + 1}/${state.total}`;
  });
  private readonly runtimeNodeMetrics = signal<FlashMapNodeMetricsMap>({});
  private readonly effectiveNodeMetrics = computed<FlashMapNodeMetricsMap>(() => ({
    ...this.nodeMetrics(),
    ...this.runtimeNodeMetrics(),
  }));
  protected readonly mapMenuOpen = signal(false);
  private copiedNodePayload: CopiedNodePayload | null = null;
  private copiedDrawingPayload: FlashMapDrawingStroke | null = null;
  private readonly findRevision = signal(0);
  protected readonly hasFindQuery = computed(() => this.findQuery().trim().length > 0);
  private readonly findInputRef = viewChild<ElementRef<HTMLInputElement>>('findInput');
  private readonly hoverInteractionsEnabled = this.detectHoverInteractions();
  protected readonly helpTitle = computed(() => {
    const translations = this.translations();
    return this.mode() === 'outline' && this.outlineEnabled()
      ? translations.helpTitleOutline
      : translations.helpTitleMap;
  });
  protected readonly helpShortcuts = computed(() =>
    this.mode() === 'outline' && this.outlineEnabled()
      ? this.translations().outlineShortcuts
      : this.translations().mapShortcuts,
  );
  private mapMenuCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private drawingPanelCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private renderedMetricNodeIds = new Set<string>();
  private presentationSlideIds: string[] = [];
  private presentationSnapshot: PresentationSnapshot | null = null;
  private presentationCurrentNodeElement: HTMLElement | null = null;
  private presentationTransitionFrame: number | null = null;
  private presentationTransitionVersion = 0;
  private isInitializing = false;
  private lastShownMindData: MindMapData | null = null;
  private activeDrawingPointerId: number | null = null;
  private activeDrawingStrokeId: number | null = null;
  private drawingInteraction: DrawingInteraction = null;
  private nextDrawingStrokeId = 1;
  private readonly laserStrokeTimers = new Map<number, ReturnType<typeof setTimeout>>();
  private readonly presentationKeydownListener = (event: KeyboardEvent): void => {
    this.handlePresentationKeydown(event);
  };

  constructor(protected readonly flashMapService: FlashMapService) {
    this.outline = new OutlineCoordinator(
      this.flashMapService,
      () => this.options(),
      () => this.editable(),
      () => this.getCurrentMeta(),
    );
    this.mode = this.outline.mode;
    this.outlineText = this.outline.outlineText;
    this.outlineError = this.outline.outlineError;
    this.selectedOutlineLine = this.outline.selectedOutlineLine;
    this.editingOutlineNodeId = this.outline.editingOutlineNodeId;
    this.outlineEnabled = this.outline.outlineEnabled;
    this.outlineTreeNodes = this.outline.outlineTreeNodes;
    // Effect to initialize when container is available
    effect(() => {
      const container = this.containerRef();
      if (container && !this.initialized() && !this.isInitializing) {
        void this.initializeFlashMap(container.nativeElement);
      }
    });

    // Effect to update mind data
    effect(() => {
      const data = this.mindData();
      if (data === null) {
        this.lastShownMindData = null;
        return;
      }

      if (this.initialized()) {
        this.showMindData(data);
      }
    });

    // Effect to handle editable changes
    effect(() => {
      const editable = this.editable();
      if (this.initialized()) {
        if (editable) {
          this.flashMapService.enableEdit();
        } else {
          this.flashMapService.disableEdit();
        }
      }
    });

    // Effect to handle theme changes
    effect(() => {
      const theme = this.theme();
      if (this.initialized()) {
        this.flashMapService.setTheme(theme);
      }
    });

    // The map can be initialized before its persisted appearance is loaded by the host.
    // Keep a subsequently supplied layout in sync with the already-running engine.
    effect(() => {
      const layoutMode = this.options().layout_mode;
      if (layoutMode && this.initialized()) {
        this.flashMapService.setLayoutMode(layoutMode);
      }
    });

    effect(() => {
      if (!this.outlineEnabled() && this.mode() !== 'map') {
        this.mode.set('map');
      }
    });

    effect(() => {
      const translations = this.translations();
      if (this.initialized()) {
        this.updateRootTitle();
      } else if (!this.rootTitle()) {
        this.rootTitle.set(translations.untitled);
      }
    });

    effect(() => {
      const metrics = this.effectiveNodeMetrics();
      if (!this.initialized()) {
        return;
      }
      this.refreshMetricsNodes(metrics);
    });

    effect(() => {
      const open = this.findOpen();
      const query = this.findQuery();
      const filter = this.findFilter();
      this.findRevision();
      if (!open) {
        this.findResults.set([]);
        this.findActiveIndex.set(0);
        return;
      }
      const results = this.buildFindResults(query, filter);
      this.findResults.set(results);
      const currentIndex = untracked(() => this.findActiveIndex());
      const nextIndex = results.length === 0 ? 0 : Math.min(currentIndex, results.length - 1);
      if (nextIndex !== currentIndex) {
        this.findActiveIndex.set(nextIndex);
      }
    });

    effect(() => {
      if (this.findOpen()) {
        queueMicrotask(() => this.focusFindInput());
      }
    });
  }

  private async initializeFlashMap(containerElement: HTMLElement): Promise<void> {
    if (this.isInitializing || this.initialized()) {
      return;
    }

    this.isInitializing = true;
    try {
      const opts = this.options();
      const pluginOptions = {
        ...(opts.plugin ?? {}),
        screenshot: {
          background: '#ffffff',
          ...(opts.plugin?.['screenshot'] ?? {}),
        },
      };
      const renderers: NodeRenderer[] = [
        new FlashcardBadgeRenderer(
          (nodeId, count) => this.flashcardClick.emit({ nodeId, count }),
          (nodeId) => this.onFlashcardPaperClick(nodeId),
        ),
        new NodeMetricsRenderer(),
        new ImageNodeRenderer((nodeId, image) => this.flashMapService.setNodeImage(nodeId, image)),
        new TextNodeRenderer(),
      ];
      const renderNodeContent = (jm: unknown, ele: HTMLElement, node: FlashMapNode): void => {
        const context: NodeRenderContext = {
          jm,
          ele,
          node,
          options: opts,
          emitFlashcardClick: undefined,
          getNodeMetrics: (nodeId) => this.effectiveNodeMetrics()[nodeId],
        };
        for (const renderer of renderers) {
          const result = renderer.render(context);
          if (result.handled && !result.continue) {
            break;
          }
        }
      };
      const baseRender = opts.view?.custom_node_render ?? null;
      const customNodeRender: NonNullable<FlashMapOptions['view']>['custom_node_render'] = (
        jm: unknown,
        ele: HTMLElement,
        node: FlashMapNode,
      ) => {
        if (!(ele instanceof HTMLElement)) {
          return true;
        }
        const baseResult = baseRender ? baseRender(jm, ele, node) : false;
        if (!baseResult) {
          renderNodeContent(jm, ele, node);
        }
        return true;
      };
      const viewOptions: FlashMapOptions['view'] = {
        enable_device_pixel_ratio: false,
        node_overflow: 'wrap',
        ...(opts.view ?? {}),
        custom_node_render: customNodeRender,
        style_toolbar: { ...this.translations().styleToolbar },
      };
      const fullOptions: FlashMapOptions = {
        container: containerElement,
        editable: this.editable(),
        theme: this.theme(),
        ...opts,
        view: viewOptions,
        plugin: pluginOptions,
      };

      await this.flashMapService.init(fullOptions);
      if (!this.flashMapService.isReady()) {
        return;
      }

      this.flashMapService.addEventListener((type: EventType, data: EventData) => {
        switch (type) {
          case EventType.SHOW:
            this.onShow.emit(data);
            this.outline.scheduleOutlineRefresh();
            this.bumpFindRevision();
            this.updateRootTitle();
            break;
          case EventType.RESIZE:
            this.onResize.emit(data);
            break;
          case EventType.EDIT:
            this.onEdit.emit(data);
            this.outline.scheduleOutlineRefresh();
            this.bumpFindRevision();
            this.updateRootTitle();
            break;
          case EventType.SELECT:
            this.clearDrawingSelection();
            this.onSelect.emit(data);
            this.outline.syncOutlineSelectionFromMap();
            break;
          case EventType.NOTE:
            if (data.node) {
              this.onFlashcardPaperClick(String(data.node));
            }
            break;
          case EventType.FLASHCARD_DECK:
            if (data.node) {
              this.flashcardDeckClick.emit({ nodeId: String(data.node) });
            }
            break;
        }
      });

      this.initialized.set(true);

      const initialData = this.mindData();
      if (initialData) {
        this.showMindData(initialData);
      }
    } finally {
      this.isInitializing = false;
    }
  }

  private showMindData(data: MindMapData): void {
    if (this.lastShownMindData === data) {
      return;
    }

    this.lastShownMindData = data;
    this.loadDrawings(data.drawings);
    this.flashMapService.show(data);
    this.refreshMetricsNodes(this.effectiveNodeMetrics());
    this.outline.scheduleOutlineRefresh();
    this.bumpFindRevision();
    this.updateRootTitle();
  }

  setNodeMetrics(nodeId: string, text: string): void {
    if (!nodeId) {
      return;
    }
    this.runtimeNodeMetrics.update((metrics) => ({
      ...metrics,
      [nodeId]: text,
    }));
  }

  setNodeMetricsMap(metrics: FlashMapNodeMetricsMap): void {
    this.runtimeNodeMetrics.set({ ...metrics });
  }

  clearNodeMetrics(nodeId: string): void {
    if (!nodeId) {
      return;
    }
    this.runtimeNodeMetrics.update((metrics) => ({
      ...metrics,
      [nodeId]: null,
    }));
  }

  clearAllNodeMetrics(): void {
    this.runtimeNodeMetrics.set({});
  }

  protected toggleMode(): void {
    this.outline.toggleMode();
  }

  protected setMode(mode: 'map' | 'outline'): void {
    this.outline.setMode(mode);
  }

  protected onOutlineTextChange(text: string): void {
    this.outline.onOutlineTextChange(text);
  }

  protected onOutlineLineSelect(lineIndex: number): void {
    this.outline.onOutlineLineSelect(lineIndex);
  }

  protected onOutlineEditFinished(): void {
    this.outline.onOutlineEditFinished();
  }

  protected onOutlineNodeSelect(nodeId: string): void {
    this.outline.onOutlineNodeSelect(nodeId);
  }

  protected onOutlineNodeEdit(data: { id: string; topic: string }): void {
    this.outline.onOutlineNodeEdit(data);
  }

  protected onOutlineNodeCreate(data: { parentId: string; topic: string }): void {
    this.outline.onOutlineNodeCreate(data);
  }

  protected onOutlineNodeDelete(nodeId: string): void {
    this.outline.onOutlineNodeDelete(nodeId);
  }

  protected onOutlineNodeIndent(nodeId: string): void {
    this.outline.onOutlineNodeIndent(nodeId);
  }

  protected onOutlineNodeOutdent(nodeId: string): void {
    this.outline.onOutlineNodeOutdent(nodeId);
  }

  protected toggleDrawingTool(tool: Exclude<DrawingTool, 'none'>): void {
    if (!this.canUseDrawingTools()) {
      return;
    }
    this.drawingTool.update((current) => (current === tool ? 'none' : tool));
  }

  toggleDrawingPanel(): void {
    this.drawingPanelVisible.update((visible) => !visible);
  }

  protected onPenButtonClick(): void {
    if (!this.drawingPanelVisible()) {
      this.drawingPanelVisible.set(true);
      return;
    }

    this.toggleDrawingTool('pen');
  }

  openDrawingPanel(): void {
    this.clearDrawingPanelCloseTimer();
    this.drawingPanelVisible.set(true);
  }

  protected onDrawingPanelMouseEnter(): void {
    if (this.hoverInteractionsEnabled) {
      this.clearDrawingPanelCloseTimer();
      this.drawingPanelVisible.set(true);
    }
  }

  protected onDrawingPanelMouseLeave(): void {
    if (this.hoverInteractionsEnabled) {
      this.scheduleDrawingPanelClose();
    }
  }

  protected selectDrawingColor(color: string): void {
    if (/^#[0-9a-f]{6}$/i.test(color)) {
      this.drawingColor.set(color);
      this.updateSelectedDrawing({ color });
    }
  }

  protected selectDrawingFillColor(color: string | null): void {
    if (color !== null && !/^#[0-9a-f]{6}$/i.test(color)) {
      return;
    }

    this.drawingFillColor.set(color);
    const selected = this.selectedDrawing();
    if (selected?.tool === 'rectangle' || selected?.tool === 'ellipse') {
      this.updateSelectedDrawing({ fill: color });
    }
  }

  protected toggleDrawingColorMenu(target: DrawingColorTarget): void {
    this.drawingColorMenu.update((current) => (current === target ? null : target));
  }

  protected toggleDrawingStrokeWidthMenu(): void {
    this.drawingStrokeWidthMenuOpen.update((open) => !open);
    this.drawingLineStyleMenuOpen.set(false);
  }

  protected toggleDrawingLineStyleMenu(): void {
    this.drawingLineStyleMenuOpen.update((open) => !open);
    this.drawingStrokeWidthMenuOpen.set(false);
  }

  protected selectDrawingLineStyle(style: DrawingLineStyle): void {
    if (!DRAWING_LINE_STYLES.includes(style)) {
      return;
    }
    this.drawingLineStyle.set(style);
    this.drawingLineStyleMenuOpen.set(false);
    this.updateSelectedDrawing({ lineStyle: style });
  }

  protected drawingLineStyleLabel(style: DrawingLineStyle): string {
    const drawing = this.translations().drawing;
    return style === 'dashed' ? drawing.strokeStyleDashed : drawing.strokeStyleSolid;
  }

  protected drawingStrokeDashArray(stroke: DrawingStroke): string | null {
    return stroke.lineStyle === 'dashed' ? '8 6' : null;
  }

  protected selectDrawingStrokeWidth(width: number): void {
    if (!DRAWING_STROKE_WIDTHS.includes(width)) {
      return;
    }
    this.drawingStrokeWidth.set(width);
    this.drawingStrokeWidthMenuOpen.set(false);
    this.updateSelectedDrawing({ lineWidth: width });
  }

  protected drawingStrokeWidthLabel(width: number): string {
    const drawing = this.translations().drawing;
    switch (width) {
      case 2:
        return drawing.strokeWidthThin;
      case 5:
        return drawing.strokeWidthThick;
      default:
        return drawing.strokeWidthMedium;
    }
  }

  protected onDrawingColorChange(event: Event): void {
    const input = event.target;
    if (input instanceof HTMLInputElement) {
      this.selectDrawingColor(input.value);
    }
  }

  protected onDrawingFillColorChange(event: Event): void {
    const input = event.target;
    if (input instanceof HTMLInputElement) {
      this.selectDrawingFillColor(input.value);
    }
  }

  protected clearPersistentDrawings(): void {
    this.drawingStrokes.update((strokes) => strokes.filter((stroke) => stroke.tool === 'laser'));
    this.clearDrawingSelection();
    this.emitDrawings();
  }

  protected sendSelectedDrawingBackward(): void {
    this.reorderSelectedArrangeableDrawing('backward');
  }

  protected bringSelectedDrawingForward(): void {
    this.reorderSelectedArrangeableDrawing('forward');
  }

  protected onMapViewPointerDown(event: PointerEvent): void {
    if (this.drawingTool() !== 'none' || this.selectedDrawingId() === null) {
      return;
    }

    const target = event.target instanceof Element ? event.target : null;
    if (
      target?.closest('[data-drawing-id]') ||
      target?.closest('.flash-map-drawing-arrange-panel')
    ) {
      return;
    }

    this.clearDrawingSelection();
  }

  protected onDrawingPointerDown(event: PointerEvent): void {
    const tool = this.drawingTool();
    if (!this.canUseDrawingTools() || this.activeDrawingPointerId !== null) {
      return;
    }
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    const point = this.getDrawingPoint(event);
    if (!point) {
      return;
    }

    const target = event.target instanceof Element ? event.target : null;
    const drawingId = Number(target?.closest('[data-drawing-id]')?.getAttribute('data-drawing-id'));
    const handle = target?.closest('[data-drawing-handle]')?.getAttribute('data-drawing-handle');
    const drawingTarget = Number.isFinite(drawingId) ? this.findDrawing(drawingId) : null;
    const currentSelection = this.selectedDrawing();
    const selectedArrowEndpoint =
      !drawingTarget && currentSelection?.tool === 'arrow'
        ? this.drawingArrowEndpointAtPoint(currentSelection, point)
        : null;
    const selected = drawingTarget ?? (selectedArrowEndpoint !== null ? currentSelection : null);
    if (tool === 'none') {
      if (!selected) {
        this.clearDrawingSelection();
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.selectDrawingExclusively(selected.id);
      this.drawingLineStyle.set(selected.lineStyle ?? 'solid');
      this.activeDrawingPointerId = event.pointerId;
      const arrowEndpoint =
        selected.tool === 'arrow'
          ? (selectedArrowEndpoint ?? this.drawingArrowEndpointAtPoint(selected, point))
          : null;
      if (arrowEndpoint !== null) {
        this.drawingInteraction = { kind: 'arrow-end', strokeId: selected.id, end: arrowEndpoint };
      } else if (handle === 'rotate') {
        this.drawingInteraction = {
          kind: 'rotate',
          strokeId: selected.id,
          center: this.drawingCenter(selected),
          initialRotation: selected.rotation ?? 0,
        };
      } else if (handle === 'arrow-start' || handle === 'arrow-end') {
        this.drawingInteraction = {
          kind: 'arrow-end',
          strokeId: selected.id,
          end: handle === 'arrow-start' ? 0 : 1,
        };
      } else if (handle) {
        this.drawingInteraction = {
          kind: 'resize',
          strokeId: selected.id,
          handle,
          initial: this.cloneStroke(selected),
        };
      } else {
        this.drawingInteraction = {
          kind: 'move',
          strokeId: selected.id,
          point,
          initial: this.cloneStroke(selected),
        };
      }
      this.captureDrawingPointer(event);
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    const strokeId = this.nextDrawingStrokeId++;
    this.activeDrawingPointerId = event.pointerId;
    this.activeDrawingStrokeId = strokeId;
    this.drawingInteraction = { kind: 'draw', strokeId };
    const stroke: DrawingStroke =
      tool === 'laser'
        ? {
            id: strokeId,
            tool,
            color: this.drawingColor(),
            isFading: false,
            lineWidth: this.drawingStrokeWidth(),
            lineStyle: this.drawingLineStyle(),
            points: [point, point],
          }
        : {
            id: strokeId,
            tool,
            color: this.drawingColor(),
            ...(tool === 'rectangle' || tool === 'ellipse'
              ? { fill: this.drawingFillColor() }
              : {}),
            lineWidth: this.drawingStrokeWidth(),
            lineStyle: this.drawingLineStyle(),
            points: [point, point],
          };
    this.drawingStrokes.update((strokes) => [...strokes, stroke]);
    this.captureDrawingPointer(event);
  }

  protected onDrawingPointerMove(event: PointerEvent): void {
    if (event.pointerId !== this.activeDrawingPointerId || !this.drawingInteraction) {
      return;
    }
    const point = this.getDrawingPoint(event);
    if (!point) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    const interaction = this.drawingInteraction;
    if (interaction.kind === 'draw') {
      this.drawingStrokes.update((strokes) =>
        strokes.map((stroke) =>
          stroke.id === interaction.strokeId
            ? { ...stroke, points: [...stroke.points, point] }
            : stroke,
        ),
      );
    } else if (interaction.kind === 'move') {
      const dx = point.x - interaction.point.x;
      const dy = point.y - interaction.point.y;
      this.replaceDrawing(interaction.strokeId, {
        ...interaction.initial,
        points: interaction.initial.points.map((current) => ({
          x: current.x + dx,
          y: current.y + dy,
        })),
      });
    } else if (interaction.kind === 'arrow-end') {
      this.drawingStrokes.update((strokes) =>
        strokes.map((stroke) => {
          if (stroke.id !== interaction.strokeId) return stroke;
          const points = [...stroke.points];
          const endpointIndex = interaction.end === 0 ? 0 : points.length - 1;
          points[endpointIndex] = point;
          return { ...stroke, points };
        }),
      );
    } else if (interaction.kind === 'resize') {
      this.resizeDrawing(interaction, point);
    } else if (interaction.kind === 'rotate') {
      const angle =
        (Math.atan2(point.y - interaction.center.y, point.x - interaction.center.x) * 180) /
          Math.PI +
        90;
      this.updateDrawing(interaction.strokeId, { rotation: Math.round(angle) });
    }
  }

  protected onDrawingPointerEnd(event: PointerEvent): void {
    if (event.pointerId !== this.activeDrawingPointerId || !this.drawingInteraction) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    const interaction = this.drawingInteraction;
    const strokeId = interaction.strokeId;
    const stroke = this.drawingStrokes().find((item) => item.id === strokeId);
    this.activeDrawingPointerId = null;
    this.activeDrawingStrokeId = null;
    this.drawingInteraction = null;
    const target = event.currentTarget;
    if (target instanceof Element && typeof target.releasePointerCapture === 'function') {
      try {
        target.releasePointerCapture(event.pointerId);
      } catch {
        // The browser may already have released capture after a pointer cancellation.
      }
    }
    if (stroke?.tool === 'laser') {
      this.startLaserStrokeFade(strokeId);
    } else {
      this.selectDrawingExclusively(strokeId);
      if (interaction.kind === 'draw') {
        this.drawingTool.set('none');
      }
      this.emitDrawings();
    }
  }

  protected drawingStrokePoints(stroke: DrawingStroke): string {
    return stroke.points.map((point) => `${point.x},${point.y}`).join(' ');
  }

  protected drawingStrokeLength(stroke: DrawingStroke): number {
    let length = 0;
    for (let index = 1; index < stroke.points.length; index += 1) {
      const previous = stroke.points[index - 1];
      const point = stroke.points[index];
      length += Math.hypot(point.x - previous.x, point.y - previous.y);
    }
    return Math.max(1, length);
  }

  protected drawingLaserGlowWidth(stroke: DrawingStroke): number {
    return stroke.lineWidth + Math.max(2, stroke.lineWidth * 0.75);
  }

  protected drawingRectangle(stroke: DrawingStroke): {
    x: number;
    y: number;
    width: number;
    height: number;
  } {
    const [start, end] = this.drawingBounds(stroke);
    return {
      x: start.x,
      y: start.y,
      width: end.x - start.x,
      height: end.y - start.y,
    };
  }

  protected drawingRotationTransform(stroke: DrawingStroke): string | null {
    const rotation = stroke.rotation ?? 0;
    if (!rotation) return null;
    const center = this.drawingCenter(stroke);
    return `rotate(${rotation} ${center.x} ${center.y})`;
  }

  protected selectedDrawingBounds(): {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null {
    const stroke = this.selectedDrawing();
    return stroke ? this.drawingRectangle(stroke) : null;
  }

  protected drawingHandleRadius(): number {
    return 6 / Math.max(this.flashMapService.viewport().zoom, 0.1);
  }

  protected drawingArrowEndHitRadius(): number {
    return this.drawingHandleRadius() * 2;
  }

  private drawingArrowEndpointAtPoint(stroke: DrawingStroke, point: DrawingPoint): 0 | 1 | null {
    const tolerance =
      DRAWING_ARROW_ENDPOINT_HIT_TARGET_SCREEN_RADIUS /
      Math.max(this.flashMapService.viewport().zoom, 0.1);
    const toleranceSquared = tolerance * tolerance;
    const isWithinTolerance = (endpoint: DrawingPoint): boolean => {
      const dx = point.x - endpoint.x;
      const dy = point.y - endpoint.y;
      return dx * dx + dy * dy <= toleranceSquared;
    };

    if (isWithinTolerance(this.drawingEndPoint(stroke))) return 1;
    if (isWithinTolerance(this.drawingStartPoint(stroke))) return 0;
    return null;
  }

  protected drawingHitStrokeWidth(stroke: DrawingStroke): number {
    const zoom = Math.max(this.flashMapService.viewport().zoom, 0.1);
    return Math.max(stroke.lineWidth, DRAWING_HIT_TARGET_SCREEN_WIDTH / zoom);
  }

  protected deleteSelectedDrawing(): void {
    const id = this.selectedDrawingId();
    if (id === null) return;
    this.drawingStrokes.update((strokes) => strokes.filter((stroke) => stroke.id !== id));
    this.clearDrawingSelection();
    this.emitDrawings();
  }

  protected drawingEllipse(stroke: DrawingStroke): {
    cx: number;
    cy: number;
    rx: number;
    ry: number;
  } {
    const rectangle = this.drawingRectangle(stroke);
    return {
      cx: rectangle.x + rectangle.width / 2,
      cy: rectangle.y + rectangle.height / 2,
      rx: rectangle.width / 2,
      ry: rectangle.height / 2,
    };
  }

  protected drawingStartPoint(stroke: DrawingStroke): DrawingPoint {
    return stroke.points[0] ?? { x: 0, y: 0 };
  }

  protected drawingEndPoint(stroke: DrawingStroke): DrawingPoint {
    return stroke.points.at(-1) ?? this.drawingStartPoint(stroke);
  }

  private drawingBounds(stroke: DrawingStroke): [DrawingPoint, DrawingPoint] {
    const start = this.drawingStartPoint(stroke);
    const end = this.drawingEndPoint(stroke);
    return [
      { x: Math.min(start.x, end.x), y: Math.min(start.y, end.y) },
      { x: Math.max(start.x, end.x), y: Math.max(start.y, end.y) },
    ];
  }

  protected shouldRenderDrawingBelowNodes(stroke: DrawingStroke): boolean {
    return (
      this.isArrangeableDrawing(stroke) && this.resolveDrawingNodeLayer(stroke) === 'below-nodes'
    );
  }

  protected shouldRenderDrawingVisualInActiveLayer(stroke: DrawingStroke): boolean {
    return !this.shouldRenderDrawingBelowNodes(stroke);
  }

  private loadDrawings(document: FlashMapDrawingDocument | undefined): void {
    const strokes = Array.isArray(document?.strokes) ? document.strokes : [];
    const normalized = strokes
      .filter((stroke) => stroke && ['pen', 'rectangle', 'ellipse', 'arrow'].includes(stroke.tool))
      .map((stroke) => this.normalizeDrawing(stroke))
      .filter((stroke): stroke is FlashMapDrawingStroke => stroke !== null);

    const usedIds = new Set<number>();
    let nextId = 1;
    const normalizedWithUniqueIds = normalized.map((stroke) => {
      let id = stroke.id;
      if (usedIds.has(id)) {
        while (usedIds.has(nextId)) {
          nextId += 1;
        }
        id = nextId;
      }

      usedIds.add(id);
      if (id >= nextId) {
        nextId = id + 1;
      }

      return id === stroke.id ? stroke : { ...stroke, id };
    });

    this.drawingStrokes.set(normalizedWithUniqueIds);
    this.clearDrawingSelection();
    this.nextDrawingStrokeId = nextId;
  }

  public getDrawingDocument(): FlashMapDrawingDocument {
    return {
      version: 1,
      strokes: this.drawingStrokes()
        .filter((stroke): stroke is FlashMapDrawingStroke => this.isPersistentDrawing(stroke))
        .map((stroke) => this.cloneStroke(stroke) as FlashMapDrawingStroke),
    };
  }

  private normalizeDrawing(stroke: FlashMapDrawingStroke): FlashMapDrawingStroke | null {
    if (
      !Number.isSafeInteger(stroke.id) ||
      stroke.id < 1 ||
      !Array.isArray(stroke.points) ||
      stroke.points.length < 2
    )
      return null;
    const points = stroke.points.map((point) => ({ x: Number(point.x), y: Number(point.y) }));
    if (points.some((point) => !Number.isFinite(point.x) || !Number.isFinite(point.y))) return null;
    const color = /^#[0-9a-f]{6}$/i.test(stroke.color) ? stroke.color : '#ef4444';
    const fill =
      typeof stroke.fill === 'string' && /^#[0-9a-f]{6}$/i.test(stroke.fill) ? stroke.fill : null;
    const lineStyle: DrawingLineStyle = stroke.lineStyle === 'dashed' ? 'dashed' : 'solid';
    const nodeLayer: DrawingNodeLayer =
      stroke.nodeLayer === 'below-nodes' ? 'below-nodes' : DEFAULT_DRAWING_NODE_LAYER;
    return {
      ...stroke,
      color,
      fill,
      lineStyle,
      nodeLayer,
      lineWidth: Math.min(
        20,
        Math.max(1, Number(stroke.lineWidth) || DEFAULT_DRAWING_STROKE_WIDTH),
      ),
      rotation: Number.isFinite(stroke.rotation) ? Number(stroke.rotation) : 0,
      points,
    };
  }

  private emitDrawings(): void {
    this.drawingChange.emit(this.getDrawingDocument());
  }

  private clearDrawingSelection(): void {
    this.selectedDrawingId.set(null);
  }

  private selectDrawingExclusively(id: number): void {
    if (this.flashMapService.getSelectedNode()) {
      this.flashMapService.selectClear();
    }
    this.selectedDrawingId.set(id);
  }

  private findDrawing(id: number): DrawingStroke | null {
    return (
      this.drawingStrokes().find((stroke) => stroke.id === id && stroke.tool !== 'laser') ?? null
    );
  }

  private isPersistentDrawing(stroke: DrawingStroke): stroke is FlashMapDrawingStroke {
    return stroke.tool !== 'laser';
  }

  private cloneStroke(stroke: DrawingStroke): DrawingStroke {
    return { ...stroke, points: stroke.points.map((point) => ({ ...point })) };
  }

  private updateSelectedDrawing(change: Partial<FlashMapDrawingStroke>): void {
    const id = this.selectedDrawingId();
    if (id === null) return;
    this.updateDrawing(id, change);
    this.emitDrawings();
  }

  private updateDrawing(id: number, change: Partial<DrawingStroke>): void {
    this.drawingStrokes.update((strokes) =>
      strokes.map((stroke) => (stroke.id === id ? { ...stroke, ...change } : stroke)),
    );
  }

  private replaceDrawing(id: number, drawing: DrawingStroke): void {
    this.drawingStrokes.update((strokes) =>
      strokes.map((stroke) => (stroke.id === id ? drawing : stroke)),
    );
  }

  private drawingCenter(stroke: DrawingStroke): DrawingPoint {
    const bounds = this.drawingRectangle(stroke);
    return { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 };
  }

  private isArrangeableDrawing(stroke: DrawingStroke): stroke is FlashMapDrawingStroke {
    return stroke.tool === 'rectangle' || stroke.tool === 'ellipse';
  }

  private resolveDrawingNodeLayer(stroke: DrawingStroke): DrawingNodeLayer {
    if (!this.isArrangeableDrawing(stroke)) {
      return DEFAULT_DRAWING_NODE_LAYER;
    }
    return stroke.nodeLayer === 'below-nodes' ? 'below-nodes' : DEFAULT_DRAWING_NODE_LAYER;
  }

  private canMoveSelectedArrangeableDrawing(direction: 'forward' | 'backward'): boolean {
    const selected = this.selectedArrangeableDrawing();
    if (!selected) {
      return false;
    }
    const strokeIndex = this.findReorderTargetIndex(
      this.drawingStrokes(),
      selected.id,
      this.resolveDrawingNodeLayer(selected),
      direction,
    );
    if (strokeIndex !== null) {
      return true;
    }
    const selectedLayer = this.resolveDrawingNodeLayer(selected);
    return direction === 'forward'
      ? selectedLayer === 'below-nodes'
      : selectedLayer === 'above-nodes';
  }

  private reorderSelectedArrangeableDrawing(direction: 'forward' | 'backward'): void {
    const selected = this.selectedArrangeableDrawing();
    if (!selected) {
      return;
    }
    const selectedLayer = this.resolveDrawingNodeLayer(selected);
    this.drawingStrokes.update((strokes) => {
      const sourceIndex = strokes.findIndex((stroke) => stroke.id === selected.id);
      if (sourceIndex < 0) {
        return strokes;
      }
      const targetIndex = this.findReorderTargetIndex(
        strokes,
        selected.id,
        selectedLayer,
        direction,
      );
      if (targetIndex !== null) {
        const next = [...strokes];
        const source = next[sourceIndex];
        next[sourceIndex] = next[targetIndex];
        next[targetIndex] = source;
        return next;
      }
      if (direction === 'forward' && selectedLayer === 'below-nodes') {
        const next = [...strokes];
        const stroke = next[sourceIndex];
        next[sourceIndex] = { ...stroke, nodeLayer: 'above-nodes' };
        return next;
      }
      if (direction === 'backward' && selectedLayer === 'above-nodes') {
        const next = [...strokes];
        const stroke = next[sourceIndex];
        next[sourceIndex] = { ...stroke, nodeLayer: 'below-nodes' };
        return next;
      }
      return strokes;
    });
    this.emitDrawings();
  }

  private findReorderTargetIndex(
    strokes: DrawingStroke[],
    selectedId: number,
    selectedLayer: DrawingNodeLayer,
    direction: 'forward' | 'backward',
  ): number | null {
    const currentIndex = strokes.findIndex((stroke) => stroke.id === selectedId);
    if (currentIndex < 0) {
      return null;
    }
    const step = direction === 'forward' ? 1 : -1;
    for (let index = currentIndex + step; index >= 0 && index < strokes.length; index += step) {
      const stroke = strokes[index];
      if (!this.isArrangeableDrawing(stroke)) {
        continue;
      }
      if (this.resolveDrawingNodeLayer(stroke) !== selectedLayer) {
        continue;
      }
      return index;
    }
    return null;
  }

  private resizeDrawing(
    interaction: Extract<DrawingInteraction, { kind: 'resize' }>,
    point: DrawingPoint,
  ): void {
    const bounds = this.drawingRectangle(interaction.initial);
    const localPoint = this.toDrawingLocalPoint(point, interaction.initial);
    const handle = interaction.handle;
    let left = bounds.x;
    let top = bounds.y;
    let right = bounds.x + bounds.width;
    let bottom = bounds.y + bounds.height;
    if (handle.includes('w')) left = localPoint.x;
    if (handle.includes('e')) right = localPoint.x;
    if (handle.includes('n')) top = localPoint.y;
    if (handle.includes('s')) bottom = localPoint.y;
    const min = 16;
    if (right - left < min) handle.includes('w') ? (left = right - min) : (right = left + min);
    if (bottom - top < min) handle.includes('n') ? (top = bottom - min) : (bottom = top + min);
    this.replaceDrawing(interaction.strokeId, {
      ...interaction.initial,
      points: [
        { x: left, y: top },
        { x: right, y: bottom },
      ],
    });
  }

  private toDrawingLocalPoint(point: DrawingPoint, stroke: DrawingStroke): DrawingPoint {
    const rotation = stroke.rotation ?? 0;
    if (!rotation) return point;

    const center = this.drawingCenter(stroke);
    const radians = (rotation * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const x = point.x - center.x;
    const y = point.y - center.y;
    return {
      x: center.x + x * cos + y * sin,
      y: center.y - x * sin + y * cos,
    };
  }

  private captureDrawingPointer(event: PointerEvent): void {
    const target = event.currentTarget;
    if (target instanceof Element && typeof target.setPointerCapture === 'function')
      target.setPointerCapture(event.pointerId);
  }

  protected onDocumentKeydown(event: KeyboardEvent): void {
    if (this.handlePresentationKeydown(event)) {
      return;
    }
    if (this.handlePresentationStartShortcut(event)) {
      return;
    }
    if (this.handleDrawingShortcut(event)) {
      return;
    }
    if (this.canUseDrawingTools() && !this.isTextInputLikeTarget(event.target)) {
      if (event.key === 'Escape' && this.selectedDrawingId() !== null) {
        event.preventDefault();
        this.clearDrawingSelection();
        return;
      }
      if (
        (event.key === 'Delete' || event.key === 'Backspace') &&
        this.selectedDrawingId() !== null
      ) {
        event.preventDefault();
        this.deleteSelectedDrawing();
        return;
      }
    }
    if (this.helpOpen() && event.key === 'Escape') {
      event.preventDefault();
      this.helpOpen.set(false);
      return;
    }
    if (this.noteOpen() && event.key === 'Escape') {
      event.preventDefault();
      this.noteOpen.set(false);
      return;
    }
    if (this.mapMenuOpen() && event.key === 'Escape') {
      event.preventDefault();
      this.mapMenuOpen.set(false);
      return;
    }
    if (this.findOpen() && event.key === 'Escape') {
      event.preventDefault();
      this.findOpen.set(false);
      return;
    }
    if (this.isFindShortcut(event) && !this.helpOpen() && !this.noteOpen()) {
      event.preventDefault();
      this.openFindPanel();
      return;
    }
    if (this.handleFindNavigation(event)) {
      return;
    }
    if (this.handleCopyPasteShortcut(event)) {
      return;
    }
    this.outline.onDocumentKeydown(event);
  }

  protected onDocumentClick(event: MouseEvent): void {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest('.flash-map-drawing-panel')) {
      this.drawingPanelVisible.set(false);
    }
    if (!target?.closest('.flash-map-drawing-width')) {
      this.drawingStrokeWidthMenuOpen.set(false);
    }
    if (!target?.closest('.flash-map-drawing-style')) {
      this.drawingLineStyleMenuOpen.set(false);
    }
    if (!target?.closest('.flash-map-drawing-color-control')) {
      this.drawingColorMenu.set(null);
    }
  }

  protected onMapDoubleClick(event: MouseEvent): void {
    if (
      this.presentationState().active ||
      !this.editable() ||
      !this.flashMapService.getEditable()
    ) {
      return;
    }
    if (this.isMapEditing()) {
      return;
    }
    const nodeId = this.resolveNodeIdFromEvent(event);
    if (!nodeId) {
      return;
    }
    this.flashMapService.selectNode(nodeId);
    this.flashMapService.beginEdit(nodeId);
  }

  private isMapEditing(): boolean {
    const container = this.containerRef()?.nativeElement;
    if (!container) {
      return false;
    }
    return !!container.querySelector('.jsmind-editor');
  }

  private buildPresentationSlideIds(): string[] {
    const root = this.flashMapService.getRoot();
    if (!root) {
      return [];
    }

    const slideIds: string[] = [root.id];
    const orderedRootChildren = [...root.children].sort((left, right) =>
      this.comparePresentationRootChildren(root, left, right),
    );

    for (const child of orderedRootChildren) {
      this.appendPresentationBranch(child, slideIds);
    }

    return slideIds;
  }

  private appendPresentationBranch(node: FlashMapNodeModel, slideIds: string[]): void {
    slideIds.push(node.id);
    for (const child of node.children) {
      this.appendPresentationBranch(child, slideIds);
    }
  }

  private comparePresentationRootChildren(
    root: FlashMapNodeModel,
    left: FlashMapNodeModel,
    right: FlashMapNodeModel,
  ): number {
    const leftAngle = this.getClockwiseAngleFromTop(root, left);
    const rightAngle = this.getClockwiseAngleFromTop(root, right);

    if (leftAngle !== rightAngle) {
      return leftAngle - rightAngle;
    }

    return FlashMapNodeModel.compare(left, right);
  }

  private getClockwiseAngleFromTop(root: FlashMapNodeModel, node: FlashMapNodeModel): number {
    const rootCenter = this.getNodeCenter(root);
    const nodeCenter = this.getNodeCenter(node);
    const angle = Math.atan2(nodeCenter.y - rootCenter.y, nodeCenter.x - rootCenter.x);
    return (angle + Math.PI / 2 + Math.PI * 2) % (Math.PI * 2);
  }

  private getNodeCenter(node: FlashMapNodeModel): { x: number; y: number } {
    const location =
      typeof node.getLocation === 'function'
        ? node.getLocation()
        : {
            x: node._data.view?.abs_x ?? 0,
            y: node._data.view?.abs_y ?? 0,
          };
    const size =
      typeof node.getSize === 'function'
        ? node.getSize()
        : {
            w: node._data.view?.width ?? 0,
            h: node._data.view?.height ?? 0,
          };
    return {
      x: location.x + size.w / 2,
      y: location.y + size.h / 2,
    };
  }

  private movePresentationSlide(delta: number): void {
    const state = this.presentationState();
    if (!state.active) {
      return;
    }

    const nextIndex = state.currentIndex + delta;
    if (nextIndex < 0 || nextIndex >= this.presentationSlideIds.length) {
      return;
    }

    const nodeId = this.presentationSlideIds[nextIndex];
    this.presentationState.set({
      active: true,
      currentIndex: nextIndex,
      total: this.presentationSlideIds.length,
      currentNodeId: nodeId,
    });
    this.focusPresentationNode(nodeId, true);
  }

  private focusPresentationNode(nodeId: string, animate: boolean = false): void {
    const node = this.flashMapService.getNode(nodeId);
    if (!node) {
      return;
    }

    this.cancelPresentationTransition();
    this.expandPresentationAncestors(node);
    this.flashMapService.selectNode(nodeId);
    requestAnimationFrame(() => {
      this.applyPresentationNodeFocus(nodeId, node, 0, animate);
    });
  }

  private applyPresentationNodeFocus(
    nodeId: string,
    node: FlashMapNodeModel,
    measurementAttempt: number,
    animate: boolean,
  ): void {
    const state = this.presentationState();
    if (!state.active || state.currentNodeId !== nodeId) {
      return;
    }

    this.setPresentationCurrentNode(nodeId);

    const zoom = this.getPresentationZoom(node, nodeId);
    if (!animate) {
      if (zoom !== null) {
        this.flashMapService.setZoom(zoom);
      }
      this.flashMapService.centerNode(nodeId);

      const zoomWasApplied = zoom !== null && this.isPresentationZoomApplied(zoom);
      if (!zoomWasApplied && measurementAttempt < PRESENTATION_MEASUREMENT_MAX_ATTEMPTS) {
        requestAnimationFrame(() => {
          this.applyPresentationNodeFocus(nodeId, node, measurementAttempt + 1, false);
        });
      }
      return;
    }

    const targetZoom = zoom ?? this.flashMapService.getZoom();
    const targetPan = this.getPresentationTargetPan(node, targetZoom);
    if (targetPan) {
      const targetViewport: PresentationViewport = { zoom: targetZoom, pan: targetPan };
      this.animatePresentationViewport(nodeId, targetViewport);
    } else {
      if (zoom !== null) {
        this.flashMapService.setZoom(zoom);
      }
      this.flashMapService.centerNode(nodeId);
    }

    const zoomWasApplied = zoom !== null && this.isPresentationZoomApplied(zoom);
    if (!zoomWasApplied && measurementAttempt < PRESENTATION_MEASUREMENT_MAX_ATTEMPTS) {
      requestAnimationFrame(() => {
        this.applyPresentationNodeFocus(nodeId, node, measurementAttempt + 1, animate);
      });
    }
  }

  private getPresentationTargetPan(
    node: FlashMapNodeModel,
    zoom: number,
  ): { x: number; y: number } | null {
    const container = this.flashMapService.getContainer();
    const width = container?.clientWidth || container?.offsetWidth || 0;
    const height = container?.clientHeight || container?.offsetHeight || 0;
    if (width <= 0 || height <= 0 || !Number.isFinite(zoom) || zoom <= 0) {
      return null;
    }

    const center = this.getNodeCenter(node);
    return {
      x: width / 2 - center.x * zoom,
      y: height / 2 - center.y * zoom,
    };
  }

  private getPresentationExitViewport(): PresentationViewport {
    const currentViewport: PresentationViewport = {
      zoom: this.flashMapService.getZoom(),
      pan: this.flashMapService.getPan(),
    };
    const container = this.flashMapService.getContainer();
    const width = container?.clientWidth || container?.offsetWidth || 0;
    const height = container?.clientHeight || container?.offsetHeight || 0;
    if (
      width <= 0 ||
      height <= 0 ||
      !Number.isFinite(currentViewport.zoom) ||
      currentViewport.zoom <= 0
    ) {
      return { zoom: PRESENTATION_EXIT_ZOOM, pan: currentViewport.pan };
    }

    const viewportCenter = {
      x: (width / 2 - currentViewport.pan.x) / currentViewport.zoom,
      y: (height / 2 - currentViewport.pan.y) / currentViewport.zoom,
    };
    return {
      zoom: PRESENTATION_EXIT_ZOOM,
      pan: {
        x: width / 2 - viewportCenter.x * PRESENTATION_EXIT_ZOOM,
        y: height / 2 - viewportCenter.y * PRESENTATION_EXIT_ZOOM,
      },
    };
  }

  private animatePresentationViewport(nodeId: string, target: PresentationViewport): void {
    const start: PresentationViewport = {
      zoom: this.flashMapService.getZoom(),
      pan: this.flashMapService.getPan(),
    };
    if (this.isPresentationViewportEqual(start, target)) {
      this.applyPresentationViewport(target);
      return;
    }

    const version = ++this.presentationTransitionVersion;
    let startTimestamp: number | null = null;
    const renderFrame = (timestamp: number): void => {
      if (!this.isPresentationTransitionActive(version, nodeId)) {
        return;
      }

      startTimestamp ??= timestamp;
      const progress = Math.min(
        1,
        (timestamp - startTimestamp) / PRESENTATION_TRANSITION_DURATION_MS,
      );
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      this.applyPresentationViewport({
        zoom: this.interpolate(start.zoom, target.zoom, easedProgress),
        pan: {
          x: this.interpolate(start.pan.x, target.pan.x, easedProgress),
          y: this.interpolate(start.pan.y, target.pan.y, easedProgress),
        },
      });

      if (progress < 1) {
        this.presentationTransitionFrame = requestAnimationFrame(renderFrame);
        return;
      }

      this.presentationTransitionFrame = null;
      this.applyPresentationViewport(target);
    };

    this.presentationTransitionFrame = requestAnimationFrame(renderFrame);
  }

  private animatePresentationExitViewport(target: PresentationViewport): void {
    const start: PresentationViewport = {
      zoom: this.flashMapService.getZoom(),
      pan: this.flashMapService.getPan(),
    };
    if (this.isPresentationViewportEqual(start, target)) {
      this.applyPresentationViewport(target);
      this.schedulePresentationExitResize();
      return;
    }

    const version = ++this.presentationTransitionVersion;
    let startTimestamp: number | null = null;
    const renderFrame = (timestamp: number): void => {
      if (this.presentationState().active || version !== this.presentationTransitionVersion) {
        return;
      }

      startTimestamp ??= timestamp;
      const progress = Math.min(
        1,
        (timestamp - startTimestamp) / PRESENTATION_TRANSITION_DURATION_MS,
      );
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      this.applyPresentationViewport({
        zoom: this.interpolate(start.zoom, target.zoom, easedProgress),
        pan: {
          x: this.interpolate(start.pan.x, target.pan.x, easedProgress),
          y: this.interpolate(start.pan.y, target.pan.y, easedProgress),
        },
      });

      if (progress < 1) {
        this.presentationTransitionFrame = requestAnimationFrame(renderFrame);
        return;
      }

      this.presentationTransitionFrame = null;
      this.applyPresentationViewport(target);
      this.schedulePresentationExitResize();
    };

    this.presentationTransitionFrame = requestAnimationFrame(renderFrame);
  }

  private applyPresentationViewport(viewport: PresentationViewport): void {
    if (Math.abs(this.flashMapService.getZoom() - viewport.zoom) >= 0.001) {
      this.flashMapService.setZoom(viewport.zoom);
    }
    this.flashMapService.setPan(viewport.pan.x, viewport.pan.y);
  }

  private cancelPresentationTransition(): void {
    this.presentationTransitionVersion += 1;
    if (this.presentationTransitionFrame !== null) {
      cancelAnimationFrame(this.presentationTransitionFrame);
      this.presentationTransitionFrame = null;
    }
  }

  private isPresentationTransitionActive(version: number, nodeId: string): boolean {
    const state = this.presentationState();
    return (
      state.active &&
      state.currentNodeId === nodeId &&
      version === this.presentationTransitionVersion
    );
  }

  private isPresentationViewportEqual(
    left: PresentationViewport,
    right: PresentationViewport,
  ): boolean {
    return (
      Math.abs(left.zoom - right.zoom) < 0.001 &&
      Math.abs(left.pan.x - right.pan.x) < 0.001 &&
      Math.abs(left.pan.y - right.pan.y) < 0.001
    );
  }

  private interpolate(start: number, end: number, progress: number): number {
    return start + (end - start) * progress;
  }

  private setPresentationCurrentNode(nodeId: string): void {
    this.presentationCurrentNodeElement?.removeAttribute('data-presentation-current');
    this.presentationCurrentNodeElement = null;

    const container = this.flashMapService.getContainer();
    const nodeElement = Array.from(
      container?.querySelectorAll<HTMLElement>('jmnode[nodeid]') ?? [],
    ).find((element) => element.getAttribute('nodeid') === nodeId);
    if (!nodeElement) {
      return;
    }

    nodeElement.setAttribute('data-presentation-current', 'true');
    this.presentationCurrentNodeElement = nodeElement;
  }

  private clearPresentationCurrentNode(): void {
    this.presentationCurrentNodeElement?.removeAttribute('data-presentation-current');
    this.presentationCurrentNodeElement = null;
  }

  private getPresentationZoom(node: FlashMapNodeModel, nodeId?: string): number | null {
    const container = this.flashMapService.getContainer();
    const measuredElement = nodeId ? this.getPresentationNodeElement(nodeId) : null;
    const currentZoom = this.flashMapService.getZoom();
    const safeCurrentZoom = Number.isFinite(currentZoom) && currentZoom > 0 ? currentZoom : 1;
    const elementBounds = measuredElement?.getBoundingClientRect();
    const renderedWidth =
      elementBounds && elementBounds.width > 0
        ? elementBounds.width / safeCurrentZoom
        : measuredElement?.offsetWidth || measuredElement?.clientWidth || 0;
    const renderedHeight =
      elementBounds && elementBounds.height > 0
        ? elementBounds.height / safeCurrentZoom
        : measuredElement?.offsetHeight || measuredElement?.clientHeight || 0;
    const modelSize =
      typeof node.getSize === 'function'
        ? node.getSize()
        : {
            w: node._data.view?.width ?? 0,
            h: node._data.view?.height ?? 0,
          };
    const nodeWidth = renderedWidth > 0 ? renderedWidth : modelSize.w;
    const nodeHeight = renderedHeight > 0 ? renderedHeight : modelSize.h;
    if (!container || nodeWidth <= 0 || nodeHeight <= 0) {
      return null;
    }

    const availableWidth = container.clientWidth || container.offsetWidth;
    const availableHeight = container.clientHeight || container.offsetHeight;
    if (availableWidth <= 0 || availableHeight <= 0) {
      return null;
    }

    const calculatedZoom = Math.min(
      (availableWidth * PRESENTATION_TARGET_WIDTH_RATIO) / nodeWidth,
      (availableHeight * PRESENTATION_TARGET_HEIGHT_RATIO) / nodeHeight,
    );
    const configuredZoom = this.options().view?.zoom;
    const configuredMax = configuredZoom?.max;
    const maximumZoom =
      typeof configuredMax === 'number' && Number.isFinite(configuredMax) && configuredMax > 0
        ? Math.min(PRESENTATION_MAX_ZOOM, configuredMax)
        : PRESENTATION_MAX_ZOOM;
    const configuredMin = configuredZoom?.min;
    const minimumZoom =
      typeof configuredMin === 'number' && Number.isFinite(configuredMin) && configuredMin > 0
        ? Math.min(configuredMin, maximumZoom)
        : 0;
    const zoom = Math.min(Math.max(calculatedZoom, minimumZoom), maximumZoom);
    return Number.isFinite(zoom) && zoom > 0 ? zoom : null;
  }

  private isPresentationZoomApplied(expectedZoom: number): boolean {
    return Math.abs(this.flashMapService.getZoom() - expectedZoom) < 0.001;
  }

  private getPresentationNodeElement(nodeId: string): HTMLElement | null {
    return (
      Array.from(
        this.flashMapService.getContainer()?.querySelectorAll<HTMLElement>('jmnode[nodeid]') ?? [],
      ).find((element) => element.getAttribute('nodeid') === nodeId) ?? null
    );
  }

  private expandPresentationAncestors(node: FlashMapNodeModel): void {
    const ancestors: FlashMapNodeModel[] = [];
    let parent = node.parent;
    while (parent) {
      ancestors.unshift(parent);
      parent = parent.parent;
    }

    for (const ancestor of ancestors) {
      this.flashMapService.expandNode(ancestor);
    }
  }

  private handlePresentationKeydown(event: KeyboardEvent): boolean {
    if (!this.presentationState().active) {
      return false;
    }

    if (this.isPresentationPanelTarget(event.target)) {
      return false;
    }

    if (event.key === 'Escape') {
      this.consumePresentationKeyboardEvent(event);
      this.exitPresentation();
      return true;
    }

    if (event.key === 'ArrowRight' || event.key === 'PageDown' || event.key === ' ') {
      this.consumePresentationKeyboardEvent(event);
      this.nextPresentationSlide();
      return true;
    }

    if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
      this.consumePresentationKeyboardEvent(event);
      this.previousPresentationSlide();
      return true;
    }

    if (this.isTargetInsideFlashMap(event.target)) {
      this.consumePresentationKeyboardEvent(event);
      return true;
    }

    return false;
  }

  private handlePresentationStartShortcut(event: KeyboardEvent): boolean {
    if (
      this.presentationState().active ||
      this.mode() !== 'map' ||
      this.helpOpen() ||
      this.noteOpen() ||
      this.findOpen() ||
      this.isMapEditing() ||
      this.isTextInputLikeTarget(event.target) ||
      event.ctrlKey ||
      event.metaKey ||
      !event.altKey ||
      event.shiftKey ||
      event.key.toLowerCase() !== 'p'
    ) {
      return false;
    }

    event.preventDefault();
    event.stopPropagation();
    this.startPresentation();
    return true;
  }

  private handleDrawingShortcut(event: KeyboardEvent): boolean {
    if (
      !this.canUseDrawingTools() ||
      this.helpOpen() ||
      this.noteOpen() ||
      this.findOpen() ||
      this.mapMenuOpen() ||
      this.isMapEditing() ||
      this.isTextInputLikeTarget(event.target) ||
      event.ctrlKey ||
      event.altKey ||
      event.metaKey
    ) {
      return false;
    }

    if (
      event.key === 'Escape' &&
      (this.drawingTool() !== 'none' ||
        this.drawingStrokeWidthMenuOpen() ||
        this.drawingLineStyleMenuOpen() ||
        this.drawingColorMenu())
    ) {
      event.preventDefault();
      event.stopPropagation();
      this.drawingTool.set('none');
      this.drawingStrokeWidthMenuOpen.set(false);
      this.drawingLineStyleMenuOpen.set(false);
      this.drawingColorMenu.set(null);
      return true;
    }

    if (!event.shiftKey) {
      return false;
    }

    const key = event.key.toLowerCase();
    if (key === 'd') {
      event.preventDefault();
      event.stopPropagation();
      this.toggleDrawingPanel();
      return true;
    }

    if (key !== 'p' && key !== 'l') {
      return false;
    }

    event.preventDefault();
    event.stopPropagation();
    this.toggleDrawingTool(key === 'p' ? 'pen' : 'laser');
    return true;
  }

  private canUseDrawingTools(): boolean {
    return (
      this.drawingEnabled() &&
      this.mode() === 'map' &&
      !this.presentationState().active &&
      this.initialized()
    );
  }

  private getDrawingPoint(event: PointerEvent): DrawingPoint | null {
    const target = event.currentTarget;
    if (!(target instanceof SVGSVGElement)) {
      return null;
    }
    const rect = target.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return null;
    }
    const screenX = event.clientX - rect.left;
    const screenY = event.clientY - rect.top;
    if (screenX < 0 || screenX > rect.width || screenY < 0 || screenY > rect.height) {
      return null;
    }

    const { zoom, pan } = this.flashMapService.viewport();
    if (!Number.isFinite(zoom) || zoom <= 0) {
      return null;
    }
    return {
      x: (screenX - pan.x) / zoom,
      y: (screenY - pan.y) / zoom,
    };
  }

  private startLaserStrokeFade(strokeId: number): void {
    this.drawingStrokes.update((strokes) =>
      strokes.map((stroke) =>
        stroke.id === strokeId && stroke.tool === 'laser' ? { ...stroke, isFading: true } : stroke,
      ),
    );
    const timer = setTimeout(() => {
      this.laserStrokeTimers.delete(strokeId);
      this.drawingStrokes.update((strokes) => strokes.filter((stroke) => stroke.id !== strokeId));
    }, LASER_STROKE_DURATION_MS);
    this.laserStrokeTimers.set(strokeId, timer);
  }

  private clearLaserStrokeTimers(): void {
    for (const timer of this.laserStrokeTimers.values()) {
      clearTimeout(timer);
    }
    this.laserStrokeTimers.clear();
  }

  private consumePresentationKeyboardEvent(event: KeyboardEvent): void {
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  private isPresentationPanelTarget(target: EventTarget | null): boolean {
    const element = target instanceof Element ? target : null;
    return element?.closest('.flash-map-presentation-panel') !== null;
  }

  private isTargetInsideFlashMap(target: EventTarget | null): boolean {
    const element = target instanceof Element ? target : null;
    return element?.closest('.flash-map-root') !== null;
  }

  private resolveNodeIdFromEvent(event: MouseEvent): string | null {
    const container = this.containerRef()?.nativeElement;
    if (!container) {
      return null;
    }

    const resolveFromElement = (element: Element | null): string | null => {
      if (!element) {
        return null;
      }
      const closest = typeof element.closest === 'function' ? element.closest('jmnode') : null;
      const nodeId = closest?.getAttribute('nodeid') ?? null;
      return nodeId && nodeId.length > 0 ? nodeId : null;
    };

    const doc = container.ownerDocument;
    if (doc && typeof doc.elementFromPoint === 'function') {
      const elementAtPoint = doc.elementFromPoint(event.clientX, event.clientY);
      const nodeId = resolveFromElement(elementAtPoint);
      if (nodeId) {
        return nodeId;
      }
    }

    const targetElement = event.target instanceof Element ? event.target : null;
    return resolveFromElement(targetElement);
  }

  private getCurrentMeta(): MindMapData['meta'] | undefined {
    const inputMeta = this.mindData()?.meta;
    if (inputMeta) {
      return inputMeta;
    }
    const data = this.flashMapService.getData('node_tree') as MindMapData;
    return data?.meta;
  }

  ngOnDestroy(): void {
    this.cancelPresentationTransition();
    this.removePresentationKeyboardListener();
    this.clearPresentationCurrentNode();
    this.clearMapMenuCloseTimer();
    this.clearDrawingPanelCloseTimer();
    this.clearLaserStrokeTimers();
    this.outline.destroy();
    this.flashMapService.destroy();
  }

  onHelpClick(): void {
    this.helpOpen.update((open) => !open);
  }

  onCloseHelp(): void {
    this.helpOpen.set(false);
  }

  onDownloadClick(): void {
    this.flashMapService.shootScreenshot();
  }

  startPresentation(): void {
    if (this.presentationState().active || !this.initialized() || !this.flashMapService.isReady()) {
      return;
    }
    this.drawingTool.set('none');
    const slideIds = this.buildPresentationSlideIds();
    if (slideIds.length === 0) {
      return;
    }

    if (this.isMapEditing()) {
      this.flashMapService.endEdit();
    }
    this.flashMapService.disableEdit();
    if (this.mode() !== 'map') {
      this.mode.set('map');
    }
    this.helpOpen.set(false);
    this.noteOpen.set(false);
    this.findOpen.set(false);
    this.mapMenuOpen.set(false);

    const selectedNode = this.flashMapService.getSelectedNode();
    this.presentationSnapshot = {
      selectedNodeId: selectedNode?.id ?? null,
      zoom: this.flashMapService.getZoom(),
      pan: this.flashMapService.getPan(),
    };
    this.presentationSlideIds = slideIds;
    this.presentationState.set({
      active: true,
      currentIndex: 0,
      total: slideIds.length,
      currentNodeId: slideIds[0],
    });
    this.addPresentationKeyboardListener();
    this.focusPresentationNode(slideIds[0]);
  }

  exitPresentation(): void {
    if (!this.presentationState().active) {
      return;
    }
    this.cancelPresentationTransition();
    const snapshot = this.presentationSnapshot;
    this.presentationSlideIds = [];
    this.presentationSnapshot = null;
    this.presentationState.set(EMPTY_PRESENTATION_STATE);
    this.removePresentationKeyboardListener();
    this.clearPresentationCurrentNode();

    if (!snapshot || !this.flashMapService.isReady()) {
      return;
    }
    if (this.editable()) {
      this.flashMapService.enableEdit();
    }
    if (snapshot.selectedNodeId && this.flashMapService.getNode(snapshot.selectedNodeId)) {
      this.flashMapService.selectNode(snapshot.selectedNodeId);
    } else {
      this.flashMapService.selectClear();
    }
    this.animatePresentationExitViewport(this.getPresentationExitViewport());
  }

  private schedulePresentationExitResize(): void {
    requestAnimationFrame(() => {
      if (!this.presentationState().active && this.flashMapService.isReady()) {
        this.flashMapService.resize();
      }
    });
  }

  private addPresentationKeyboardListener(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.document.addEventListener('keydown', this.presentationKeydownListener, true);
  }

  private removePresentationKeyboardListener(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.document.removeEventListener('keydown', this.presentationKeydownListener, true);
  }

  nextPresentationSlide(): void {
    this.movePresentationSlide(1);
  }

  previousPresentationSlide(): void {
    this.movePresentationSlide(-1);
  }

  onShareClick(): void {
    // Handle share button click
    console.log('Share button clicked');
  }

  onNoteClose(): void {
    this.noteOpen.set(false);
  }

  onNoteTextChange(text: string): void {
    this.noteText.set(text);
    const nodeId = this.activeNoteNodeId();
    if (!nodeId) {
      return;
    }
    const node = this.flashMapService.getNode(nodeId);
    if (!node) {
      return;
    }
    if (!hasMeaningfulNoteText(text)) {
      if (node.data?.data) {
        delete node.data.data.note;
      }
      if (node.data) {
        delete node.data.note;
      }
    } else if (node.data?.data) {
      const existing = this.resolveNote(node.data.data.note);
      node.data.data.note = { ...existing, text };
    } else if (node.data) {
      const existing = this.resolveNote(node.data.note);
      node.data.note = { ...existing, text };
    }
    this.bumpFindRevision();
  }

  openNodeNote(nodeId: string): void {
    if (!nodeId) {
      return;
    }
    this.onFlashcardPaperClick(nodeId);
  }

  openFlashcardDeck(nodeId: string): void {
    if (!nodeId) {
      return;
    }
    this.flashcardDeckClick.emit({ nodeId });
  }

  private onFlashcardPaperClick(nodeId: string): void {
    this.flashcardPaperClick.emit({ nodeId });
    const node = this.flashMapService.getNode(nodeId);
    const note = this.resolveNote(node?.data?.data?.note ?? node?.data?.note);
    this.noteText.set(note?.text ?? '');
    this.noteSubtitle.set(node?.topic ?? '');
    this.activeNoteNodeId.set(nodeId);
    this.noteOpen.set(true);
  }

  private resolveNote(note: NoteMeta | undefined): NoteMeta | undefined {
    return note ?? undefined;
  }

  protected onFindQueryInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.findQuery.set(target?.value ?? '');
    this.findActiveIndex.set(0);
  }

  protected onFindResultClick(result: FindResult, index: number): void {
    this.findActiveIndex.set(index);
    this.selectFindResult(result);
  }

  protected onFindClose(): void {
    this.findOpen.set(false);
  }

  protected onFindButtonClick(): void {
    this.openFindPanel();
  }

  protected onMapMenuToggle(): void {
    this.clearMapMenuCloseTimer();
    this.mapMenuOpen.update((value) => !value);
  }

  protected onMapTitlePanelMouseEnter(): void {
    if (!this.hoverInteractionsEnabled) {
      return;
    }
    this.clearMapMenuCloseTimer();
    this.mapMenuOpen.set(true);
  }

  protected onMapTitlePanelMouseLeave(): void {
    if (!this.hoverInteractionsEnabled) {
      return;
    }
    this.scheduleMapMenuClose();
  }

  protected onMapMenuMouseEnter(): void {
    if (!this.hoverInteractionsEnabled) {
      return;
    }
    this.clearMapMenuCloseTimer();
  }

  protected onMapMenuMouseLeave(): void {
    if (!this.hoverInteractionsEnabled) {
      return;
    }
    this.scheduleMapMenuClose();
  }

  protected onMapMenuClose(): void {
    this.clearMapMenuCloseTimer();
    this.mapMenuOpen.set(false);
  }

  protected onMapMenuItemClick(action: MapMenuAction): void {
    this.clearMapMenuCloseTimer();
    this.mapMenuOpen.set(false);
    switch (action) {
      case 'help':
        this.onHelpClick();
        break;
      case 'download':
        this.onDownloadClick();
        break;
      case 'share':
        this.onShareClick();
        break;
      default:
        break;
    }
  }

  trackByFindResult(index: number, result: FindResult): string {
    return `${result.id}|${result.type}|${index}`;
  }

  private openFindPanel(): void {
    if (!this.findOpen()) {
      this.findOpen.set(true);
    }
    this.focusFindInput();
  }

  private focusFindInput(): void {
    const input = this.findInputRef()?.nativeElement;
    if (!input) {
      return;
    }
    input.focus();
    input.select();
  }

  private handleFindNavigation(event: KeyboardEvent): boolean {
    if (!this.findOpen()) {
      return false;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      this.moveFindSelection(1);
      return true;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      this.moveFindSelection(-1);
      return true;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.confirmFindSelection();
      return true;
    }
    return false;
  }

  private moveFindSelection(delta: number): void {
    const results = this.findResults();
    if (results.length === 0) {
      return;
    }
    const nextIndex = (this.findActiveIndex() + delta + results.length) % results.length;
    this.findActiveIndex.set(nextIndex);
  }

  private confirmFindSelection(): void {
    const results = this.findResults();
    if (results.length === 0) {
      return;
    }
    const result = results[this.findActiveIndex()];
    if (result) {
      this.selectFindResult(result);
    }
  }

  private selectFindResult(result: FindResult): void {
    if (!result?.id) {
      return;
    }
    this.flashMapService.selectNode(result.id);
    this.flashMapService.centerNode(result.id);
    this.findOpen.set(false);
  }

  private isFindShortcut(event: KeyboardEvent): boolean {
    if (!event.ctrlKey || event.altKey || event.shiftKey) {
      return false;
    }
    return event.key === 'f' || event.key === 'F';
  }

  private handleCopyPasteShortcut(event: KeyboardEvent): boolean {
    if (this.mode() === 'outline' && this.outlineEnabled()) {
      return this.handleOutlineClipboardShortcut(event);
    }
    if (!event.ctrlKey || event.altKey || event.shiftKey) {
      return false;
    }
    if (this.mode() !== 'map') {
      return false;
    }
    if (this.isTextInputLikeTarget(event.target) || this.isMapEditing()) {
      return false;
    }

    const key = event.key.toLowerCase();
    if (this.handleDrawingClipboardShortcut(event, key)) {
      return true;
    }

    if (key === 'c') {
      return this.copySelectedNode(event);
    }
    if (key === 'x') {
      return this.cutSelectedNode(event);
    }
    if (key === 'v') {
      return this.pasteCopiedNode(event);
    }
    return false;
  }

  private handleDrawingClipboardShortcut(event: KeyboardEvent, key: string): boolean {
    if (!this.canUseDrawingTools()) {
      return false;
    }

    if (key === 'c') {
      return this.copySelectedDrawing(event);
    }
    if (key === 'x') {
      return this.cutSelectedDrawing(event);
    }
    if (
      key === 'v' &&
      this.copiedDrawingPayload &&
      (this.selectedDrawingId() !== null || !this.copiedNodePayload)
    ) {
      return this.pasteCopiedDrawing(event);
    }

    return false;
  }

  private handleOutlineClipboardShortcut(event: KeyboardEvent): boolean {
    if (!event.ctrlKey || event.altKey || event.shiftKey) {
      return false;
    }
    if (this.isTextInputLikeTarget(event.target) || this.editingOutlineNodeId()) {
      return false;
    }

    const key = event.key.toLowerCase();
    if (key === 'c') {
      return this.copySelectedOutlineNode(event);
    }
    if (key === 'v') {
      return this.pasteTabbedOutlineText(event);
    }
    return false;
  }

  private copySelectedNode(event: KeyboardEvent): boolean {
    const selectedNode = this.flashMapService.getSelectedNode();
    if (!selectedNode) {
      return false;
    }
    this.copiedNodePayload = this.serializeNodeForClipboard(selectedNode);
    event.preventDefault();
    event.stopPropagation();
    return true;
  }

  private copySelectedDrawing(event: KeyboardEvent): boolean {
    if (!this.copySelectedDrawingToClipboard()) {
      return false;
    }

    event.preventDefault();
    event.stopPropagation();
    return true;
  }

  private cutSelectedDrawing(event: KeyboardEvent): boolean {
    if (!this.copySelectedDrawingToClipboard()) {
      return false;
    }

    this.deleteSelectedDrawing();
    event.preventDefault();
    event.stopPropagation();
    return true;
  }

  private pasteCopiedDrawing(event: KeyboardEvent): boolean {
    const copiedDrawing = this.copiedDrawingPayload;
    if (!copiedDrawing) {
      return false;
    }

    const zoom = Math.max(this.flashMapService.viewport().zoom, 0.1);
    const offset = Math.max(12, 24 / zoom);
    const nextDrawing: FlashMapDrawingStroke = {
      ...(this.cloneStroke(copiedDrawing) as FlashMapDrawingStroke),
      id: this.nextDrawingStrokeId++,
      points: copiedDrawing.points.map((point) => ({
        x: point.x + offset,
        y: point.y + offset,
      })),
    };

    this.drawingStrokes.update((strokes) => [...strokes, nextDrawing]);
    this.selectDrawingExclusively(nextDrawing.id);
    this.emitDrawings();

    event.preventDefault();
    event.stopPropagation();
    return true;
  }

  private copySelectedDrawingToClipboard(): boolean {
    const selectedDrawing = this.selectedDrawing();
    if (!selectedDrawing || !this.isPersistentDrawing(selectedDrawing)) {
      return false;
    }

    this.copiedDrawingPayload = this.cloneStroke(selectedDrawing) as FlashMapDrawingStroke;
    return true;
  }

  private cutSelectedNode(event: KeyboardEvent): boolean {
    if (!this.editable() || !this.flashMapService.getEditable()) {
      return false;
    }
    const selectedNode = this.flashMapService.getSelectedNode();
    if (!selectedNode || selectedNode.isroot) {
      return false;
    }

    this.copiedNodePayload = this.serializeNodeForClipboard(selectedNode);
    const removed = this.flashMapService.removeNode(selectedNode.id);
    if (!removed) {
      return false;
    }

    this.outline.scheduleOutlineRefresh();
    this.bumpFindRevision();
    this.updateRootTitle();

    event.preventDefault();
    event.stopPropagation();
    return true;
  }

  private pasteCopiedNode(event: KeyboardEvent): boolean {
    if (!this.copiedNodePayload || !this.editable() || !this.flashMapService.getEditable()) {
      return false;
    }
    const parentNode = this.flashMapService.getSelectedNode() ?? this.flashMapService.getRoot();
    if (!parentNode) {
      return false;
    }
    const createdNodeId = this.insertClipboardNodeAsChild(parentNode.id, this.copiedNodePayload);
    if (!createdNodeId) {
      return false;
    }

    this.flashMapService.selectNode(createdNodeId);
    this.flashMapService.centerNode(createdNodeId);
    this.outline.scheduleOutlineRefresh();
    this.bumpFindRevision();
    this.updateRootTitle();

    event.preventDefault();
    event.stopPropagation();
    return true;
  }

  private copySelectedOutlineNode(event: KeyboardEvent): boolean {
    const selectedNode = this.flashMapService.getSelectedNode();
    const clipboard = globalThis.navigator?.clipboard;
    if (!selectedNode || !clipboard?.writeText) {
      return false;
    }

    const text = toTabbedOutlineFragment(selectedNode);
    event.preventDefault();
    event.stopPropagation();
    void clipboard.writeText(text).catch(() => undefined);
    return true;
  }

  private pasteTabbedOutlineText(event: KeyboardEvent): boolean {
    const clipboard = globalThis.navigator?.clipboard;
    if (!clipboard?.readText || !this.editable() || !this.flashMapService.getEditable()) {
      return false;
    }

    event.preventDefault();
    event.stopPropagation();
    void this.pasteTabbedOutlineTextFromClipboard(clipboard);
    return true;
  }

  private async pasteTabbedOutlineTextFromClipboard(clipboard: Clipboard): Promise<void> {
    let text = '';
    try {
      text = await clipboard.readText();
    } catch {
      return;
    }

    const parsed = fromTabbedOutlineFragment(text);
    if (parsed.error) {
      this.outlineError.set(parsed.error);
      return;
    }
    if (parsed.nodes.length === 0) {
      return;
    }

    const parentNode = this.flashMapService.getSelectedNode() ?? this.flashMapService.getRoot();
    if (!parentNode) {
      return;
    }

    const firstCreatedNodeId = this.insertOutlineFragmentAsChildren(parentNode.id, parsed.nodes);
    if (!firstCreatedNodeId) {
      return;
    }

    this.outlineError.set(null);
    this.flashMapService.selectNode(firstCreatedNodeId);
    this.flashMapService.centerNode(firstCreatedNodeId);
    this.outline.scheduleOutlineRefresh();
    this.bumpFindRevision();
    this.updateRootTitle();
  }

  private insertOutlineFragmentAsChildren(
    parentId: string,
    nodes: OutlineFragmentNode[],
  ): string | null {
    let firstCreatedNodeId: string | null = null;
    for (const node of nodes) {
      const createdNodeId = this.insertOutlineFragmentNodeAsChild(parentId, node);
      firstCreatedNodeId ??= createdNodeId;
    }
    return firstCreatedNodeId;
  }

  private insertOutlineFragmentNodeAsChild(
    parentId: string,
    fragmentNode: OutlineFragmentNode,
  ): string | null {
    const nodeId = this.createNodeId();
    const newNode = this.flashMapService.addNode(parentId, nodeId, fragmentNode.topic);
    if (!newNode) {
      return null;
    }

    for (const child of fragmentNode.children) {
      this.insertOutlineFragmentNodeAsChild(nodeId, child);
    }
    return nodeId;
  }

  private insertClipboardNodeAsChild(parentId: string, payload: CopiedNodePayload): string | null {
    const nodeId = this.createNodeId();
    const newNode = this.flashMapService.addNode(
      parentId,
      nodeId,
      payload.topic,
      payload.data,
      payload.direction,
    );
    if (!newNode) {
      return null;
    }

    for (const child of payload.children) {
      this.insertClipboardNodeAsChild(nodeId, child);
    }

    if (!payload.expanded) {
      this.flashMapService.collapseNode(nodeId);
    }
    return nodeId;
  }

  private serializeNodeForClipboard(node: FlashMapNodeModel): CopiedNodePayload {
    return {
      topic: node.topic ?? '',
      data: this.deepCloneNodeData(node.data),
      direction: node.direction,
      expanded: node.expanded !== false,
      children: (node.children ?? []).map((child) => this.serializeNodeForClipboard(child)),
    };
  }

  private deepCloneNodeData(data: Record<string, unknown> | undefined): Record<string, unknown> {
    if (!data) {
      return {};
    }
    const structuredCloneApi = globalThis.structuredClone;
    if (typeof structuredCloneApi === 'function') {
      try {
        return structuredCloneApi(data) as Record<string, unknown>;
      } catch {
        // fallback to JSON clone below
      }
    }
    try {
      return JSON.parse(JSON.stringify(data)) as Record<string, unknown>;
    } catch {
      return {};
    }
  }

  private createNodeId(): string {
    const cryptoApi = globalThis.crypto;
    if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
      return `node-${cryptoApi.randomUUID()}`;
    }
    return `node-${Math.random().toString(36).slice(2, 10)}`;
  }

  private isTextInputLikeTarget(target: EventTarget | null): boolean {
    const element = target as HTMLElement | null;
    if (!element) {
      return false;
    }
    if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
      return true;
    }
    return element.isContentEditable;
  }

  private buildFindResults(query: string, filter: FindFilter): FindResult[] {
    const trimmed = query.trim();
    if (!trimmed) {
      return [];
    }
    const normalizedQuery = this.normalizeSearchText(trimmed);
    if (!normalizedQuery) {
      return [];
    }
    const queryParts = normalizedQuery.split(' ').filter(Boolean);
    if (queryParts.length === 0) {
      return [];
    }

    const results: FindResult[] = [];
    const nodes = this.collectNodes();
    for (const node of nodes) {
      if (!node) {
        continue;
      }
      const untitled = this.translations().untitled;
      const rawTitle = node.topic?.trim() || untitled;
      const title = this.toPlainText(rawTitle) || untitled;
      const noteText = this.extractNoteText(node);
      const noteTextPlain = this.toPlainText(noteText);
      const topicMatch = this.matchesQuery(title, queryParts);
      const noteMatch = this.matchesQuery(noteTextPlain, queryParts);

      if (filter === 'topic' || filter === 'outline') {
        if (topicMatch) {
          results.push({ id: node.id, title, type: 'topic' });
        }
      } else if (filter === 'note') {
        if (noteMatch) {
          results.push({
            id: node.id,
            title,
            type: 'note',
            snippet: this.buildSnippet(noteTextPlain),
          });
        }
      } else {
        if (topicMatch) {
          results.push({ id: node.id, title, type: 'topic' });
        } else if (noteMatch) {
          results.push({
            id: node.id,
            title,
            type: 'note',
            snippet: this.buildSnippet(noteTextPlain),
          });
        }
      }
      if (results.length >= 100) {
        break;
      }
    }
    return results;
  }

  private collectNodes(): FlashMapNodeModel[] {
    const root = this.flashMapService.getRoot();
    if (!root) {
      return [];
    }
    const nodes: FlashMapNodeModel[] = [];
    const visit = (node: FlashMapNodeModel): void => {
      nodes.push(node);
      if (node.children?.length) {
        node.children.forEach((child) => visit(child));
      }
    };
    visit(root);
    return nodes;
  }

  private extractNoteText(node: FlashMapNodeModel): string {
    const directNote = node.data?.note?.text ?? '';
    const nestedNote = (node.data?.data as FlashcardMeta | undefined)?.note?.text ?? '';
    return directNote || nestedNote || '';
  }

  private matchesQuery(value: string, queryParts: string[]): boolean {
    if (!value) {
      return false;
    }
    const normalized = this.normalizeSearchText(value);
    if (!normalized) {
      return false;
    }
    return queryParts.every((part) => normalized.includes(part));
  }

  private normalizeSearchText(value: string): string {
    return value
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  private buildSnippet(value: string): string {
    const cleaned = this.normalizeSnippetText(value);
    if (!cleaned) {
      return '';
    }
    if (cleaned.length <= 120) {
      return cleaned;
    }
    return `${cleaned.slice(0, 120)}...`;
  }

  private normalizeSnippetText(value: string): string {
    return value
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private updateRootTitle(): void {
    const root = this.flashMapService.getRoot();
    const rawTitle = root?.topic ?? this.mindData()?.meta?.name ?? '';
    const cleaned = this.normalizeTitleText(rawTitle);
    this.rootTitle.set(cleaned || this.translations().untitled);
  }

  private normalizeTitleText(value: string): string {
    return value
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private toPlainText(value: string): string {
    if (!value) {
      return '';
    }
    return this.normalizeTitleText(MarkdownParser.parse(value));
  }

  private bumpFindRevision(): void {
    this.findRevision.update((value) => value + 1);
  }

  private scheduleMapMenuClose(): void {
    this.clearMapMenuCloseTimer();
    this.mapMenuCloseTimer = setTimeout(() => {
      this.mapMenuOpen.set(false);
      this.mapMenuCloseTimer = null;
    }, 320);
  }

  private clearMapMenuCloseTimer(): void {
    if (this.mapMenuCloseTimer !== null) {
      clearTimeout(this.mapMenuCloseTimer);
      this.mapMenuCloseTimer = null;
    }
  }

  private scheduleDrawingPanelClose(): void {
    this.clearDrawingPanelCloseTimer();
    this.drawingPanelCloseTimer = setTimeout(() => {
      this.drawingPanelVisible.set(false);
      this.drawingPanelCloseTimer = null;
    }, 320);
  }

  private clearDrawingPanelCloseTimer(): void {
    if (this.drawingPanelCloseTimer !== null) {
      clearTimeout(this.drawingPanelCloseTimer);
      this.drawingPanelCloseTimer = null;
    }
  }

  private refreshMetricsNodes(metrics: FlashMapNodeMetricsMap): void {
    const nextMetricNodeIds = new Set(
      Object.entries(metrics)
        .filter(([, value]) => {
          if (typeof value === 'string') {
            return value.trim().length > 0;
          }
          return typeof value?.text === 'string' && value.text.trim().length > 0;
        })
        .map(([nodeId]) => nodeId),
    );
    const nodeIdsToRefresh = new Set([...this.renderedMetricNodeIds, ...nextMetricNodeIds]);
    this.renderedMetricNodeIds = nextMetricNodeIds;

    for (const nodeId of nodeIdsToRefresh) {
      this.flashMapService.refreshNode(nodeId);
    }
  }

  private detectHoverInteractions(): boolean {
    const matchMediaApi = globalThis.matchMedia;
    if (typeof matchMediaApi !== 'function') {
      return false;
    }
    return matchMediaApi('(hover: hover) and (pointer: fine)').matches;
  }
}
